import {
  createStructuredLogger,
  type ExceptionContext,
  type LoggerLike,
  type LogValue,
  normalizeError,
  type ObservedUser,
} from '@hypersense/observability/shared';
import type { AnyRouter } from '@tanstack/react-router';

import { env } from './env';

type SentryReactModuleT = typeof import('@sentry/react');

const logger = createStructuredLogger(createConsoleLogger());
let sentryInitialized = false;
let sentryModulePromise: null | Promise<SentryReactModuleT> = null;

export function initializeWebObservability(input: { router: AnyRouter }) {
  const sentryDsn = env.VITE_SENTRY_DSN;

  if (!canUseBrowserSentry() || !sentryDsn || sentryInitialized) {
    return;
  }

  loadSentryReact()
    .then((Sentry) => {
      if (sentryInitialized) {
        return;
      }

      Sentry.init({
        dsn: sentryDsn,
        environment: env.VITE_APP_ENV,
        ignoreErrors: [
          'Failed to fetch',
          'Load failed',
          'NetworkError',
          'ResizeObserver loop',
          'User denied',
          'User rejected',
        ],
        integrations: [
          Sentry.tanstackRouterBrowserTracingIntegration(input.router),
        ],
        release: env.VITE_RELEASE,
        sendDefaultPii: true,
        tracePropagationTargets: readTracePropagationTargets(),
        tracesSampleRate: env.VITE_APP_ENV === 'production' ? 0.1 : 1,
      });

      sentryInitialized = true;
    })
    .catch((error) => {
      logger.warn('observability.sentry_init_failed', {
        error,
      });
    });
}

export const webObservability = {
  captureException(error: unknown, context?: ExceptionContext) {
    logger.error('exception.captured', {
      ...normalizeError(error),
      requestId: context?.requestId,
      tags: context?.tags,
      userEmail: context?.user?.email ?? undefined,
      userId: context?.user?.id,
      userWallet: context?.user?.wallet ?? undefined,
    });

    if (!env.VITE_SENTRY_DSN) {
      return;
    }

    if (!canUseBrowserSentry()) {
      return;
    }

    loadSentryReact()
      .then((Sentry) => {
        if (!sentryInitialized) {
          return;
        }

        Sentry.withScope((scope) => {
          for (const [key, value] of Object.entries(context?.tags ?? {})) {
            scope.setTag(key, value);
          }

          if (context?.requestId) {
            scope.setTag('request_id', context.requestId);
          }

          if (context?.user) {
            scope.setUser({
              email: context.user.email ?? undefined,
              id: context.user.id,
              wallet: context.user.wallet ?? undefined,
            });
          }

          if (context?.extra) {
            scope.setContext('extra', readSentryContext(context.extra));
          }

          Sentry.captureException(
            error instanceof Error ? error : new Error(String(error)),
          );
        });
      })
      .catch((loadError) => {
        logger.warn('observability.sentry_capture_failed', {
          error: loadError,
        });
      });
  },
  clearUser() {
    if (!canUseBrowserSentry()) {
      return;
    }

    loadSentryReact()
      .then((Sentry) => {
        if (!sentryInitialized) {
          return;
        }

        Sentry.setUser(null);
      })
      .catch((error) => {
        logger.warn('observability.sentry_clear_user_failed', {
          error,
        });
      });
  },
  logger,
  reactErrorHandler() {
    return (error: unknown, errorInfo: unknown) => {
      webObservability.captureException(error, {
        extra: {
          errorInfo,
        },
        tags: {
          operation: 'react.render',
        },
      });
    };
  },
  setUser(user: ObservedUser) {
    if (!canUseBrowserSentry()) {
      return;
    }

    loadSentryReact()
      .then((Sentry) => {
        if (!sentryInitialized) {
          return;
        }

        Sentry.setUser({
          email: user.email ?? undefined,
          id: user.id,
          wallet: user.wallet ?? undefined,
        });
      })
      .catch((error) => {
        logger.warn('observability.sentry_set_user_failed', {
          error,
        });
      });
  },
};

function canUseBrowserSentry() {
  return typeof window !== 'undefined';
}

function readSentryContext(input: Record<string, unknown>) {
  const normalizedContext: Record<string, LogValue> = {};

  for (const [key, value] of Object.entries(input)) {
    normalizedContext[key] = readLogValue(value);
  }

  return normalizedContext;
}

function readLogValue(value: unknown): LogValue {
  if (
    value === null ||
    value === undefined ||
    typeof value === 'boolean' ||
    typeof value === 'number' ||
    typeof value === 'string'
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(readLogValue);
  }

  if (value instanceof Error) {
    return {
      message: value.message,
      name: value.name,
      stack: value.stack ?? null,
    };
  }

  if (typeof value === 'object') {
    const normalizedRecord: { [key: string]: LogValue } = {};

    for (const [key, nestedValue] of Object.entries(value)) {
      normalizedRecord[key] = readLogValue(nestedValue);
    }

    return normalizedRecord;
  }

  return String(value);
}

function readTracePropagationTargets() {
  const defaultTargets: Array<RegExp | string> = ['localhost', /^\//];
  const apiTarget = readApiTracePropagationTarget();

  if (!apiTarget) {
    return defaultTargets;
  }

  return [...defaultTargets, apiTarget];
}

function readApiTracePropagationTarget() {
  try {
    return new URL(env.VITE_API_BASE_URL).origin;
  } catch {
    return null;
  }
}

function createConsoleLogger(): LoggerLike {
  return {
    child(bindings) {
      return createBoundConsoleLogger(bindings);
    },
    debug(context, message) {
      console.debug(message, context);
    },
    error(context, message) {
      console.error(message, context);
    },
    info(context, message) {
      console.info(message, context);
    },
    warn(context, message) {
      console.warn(message, context);
    },
  };
}

function loadSentryReact() {
  if (sentryModulePromise !== null) {
    return sentryModulePromise;
  }

  sentryModulePromise = import('@sentry/react');

  return sentryModulePromise;
}

function createBoundConsoleLogger(
  bindings: Record<string, unknown>,
): LoggerLike {
  return {
    child(extraBindings) {
      return createBoundConsoleLogger({
        ...bindings,
        ...extraBindings,
      });
    },
    debug(context, message) {
      console.debug(message, {
        ...bindings,
        ...context,
      });
    },
    error(context, message) {
      console.error(message, {
        ...bindings,
        ...context,
      });
    },
    info(context, message) {
      console.info(message, {
        ...bindings,
        ...context,
      });
    },
    warn(context, message) {
      console.warn(message, {
        ...bindings,
        ...context,
      });
    },
  };
}
